import { notifyError } from "@/lib/notify-error";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useMutation } from "convex/react";
import { Check, Loader2, Search, X } from "lucide-react";
import { toast } from "sonner";

import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { RoleBadge } from "@/components/semantic-badges";
import { IconButton } from "@/components/ui/icon-button";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

export interface ProjectAccessMember {
  _id: string;
  name?: string;
  email: string;
  orgRole?: string;
  hasProjectAccess: boolean;
}

interface ProjectAccessDialogBaseProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectName?: string;
  lockedUserIds?: string[];
  lockedUserLabel?: string;
}

interface PersistedProjectAccessDialogProps extends ProjectAccessDialogBaseProps {
  projectId: Id<"projects">;
  members?: never;
  selectedUserIds?: never;
  onSelectedUserIdsChange?: never;
}

interface DraftProjectAccessDialogProps extends ProjectAccessDialogBaseProps {
  projectId?: never;
  members: Array<{
    _id: string;
    name?: string;
    email: string;
    orgRole?: string;
  }>;
  selectedUserIds: string[];
  onSelectedUserIdsChange: (userIds: string[]) => void;
}

type ProjectAccessDialogProps =
  | PersistedProjectAccessDialogProps
  | DraftProjectAccessDialogProps;

function getDisplayName(member: Pick<ProjectAccessMember, "name" | "email">) {
  return member.name?.trim() || member.email;
}

function getNormalizedText(value: string | undefined) {
  return value?.toLowerCase() ?? "";
}

/** Members and reviewers see only projects they are assigned to; owners and admins see all. */
function isMemberRole(member: Pick<ProjectAccessMember, "orgRole">) {
  const role = member.orgRole ?? "member";
  return role === "member" || role === "reviewer";
}

export function ProjectAccessDialog(props: ProjectAccessDialogProps) {
  const [search, setSearch] = useState("");
  const [updatingMemberId, setUpdatingMemberId] = useState<string | null>(null);
  const setMemberAccess = useMutation(api.projects.setMemberProjectAccess);
  const persistedProjectId = "projectId" in props ? props.projectId : undefined;
  const draftMembers = "members" in props ? props.members : undefined;
  const selectedUserIds =
    ("selectedUserIds" in props ? props.selectedUserIds : undefined) ?? [];
  const onSelectedUserIdsChange =
    "onSelectedUserIdsChange" in props ? props.onSelectedUserIdsChange : undefined;

  const { data: persistedMembers } = useQuery(
    convexQuery(
      api.projects.listMembersWithProjectAccess,
      persistedProjectId && props.open ? { projectId: persistedProjectId } : "skip"
    )
  );

  const isPersisted = persistedProjectId !== undefined;
  const isLoading = isPersisted && persistedMembers === undefined;
  const lockedUserIds = props.lockedUserIds ?? [];
  const lockedUserLabel = props.lockedUserLabel ?? "always included";
  const normalizedSearch = search.trim().toLowerCase();
  const searchResultLimit = normalizedSearch ? 8 : 5;

  const members: ProjectAccessMember[] = isPersisted
    ? (persistedMembers ?? []).map((member) => ({
        _id: member._id,
        name: member.name,
        email: member.email,
        orgRole: member.orgRole,
        hasProjectAccess: member.hasProjectAccess,
      }))
    : (draftMembers ?? []).map((member) => ({
        _id: member._id,
        name: member.name,
        email: member.email,
        orgRole: member.orgRole,
        hasProjectAccess: selectedUserIds.includes(member._id),
      }));

  const lockedUserIdSet = new Set(lockedUserIds);
  const currentMembers = members
    .filter(
      (member) =>
        member.hasProjectAccess ||
        !isMemberRole(member) ||
        (!isPersisted && lockedUserIdSet.has(member._id))
    )
    .sort((a, b) => getDisplayName(a).localeCompare(getDisplayName(b)));
  const addableMembers = members
    .filter(
      (member) =>
        isMemberRole(member) &&
        !member.hasProjectAccess &&
        (!lockedUserIdSet.has(member._id) || isPersisted)
    )
    .filter((member) => {
      if (!normalizedSearch) return true;
      return (
        getNormalizedText(member.name).includes(normalizedSearch) ||
        getNormalizedText(member.email).includes(normalizedSearch)
      );
    })
    .sort((a, b) => getDisplayName(a).localeCompare(getDisplayName(b)))
    .slice(0, searchResultLimit);

  const handleGrantAccess = async (userId: string) => {
    setUpdatingMemberId(userId);
    try {
      if (persistedProjectId) {
        await setMemberAccess({
          projectId: persistedProjectId,
          userId: userId as Id<"users">,
          hasAccess: true,
        });
        toast.success("Member access granted");
      } else {
        onSelectedUserIdsChange?.(
          selectedUserIds.includes(userId) ? selectedUserIds : [...selectedUserIds, userId]
        );
      }
      setSearch("");
    } catch (error) {
      notifyError(error, "Failed to update project access");
      console.error("Access update error:", error);
    } finally {
      setUpdatingMemberId(null);
    }
  };

  const handleRevokeAccess = async (userId: string) => {
    if (lockedUserIdSet.has(userId)) return;

    setUpdatingMemberId(userId);
    try {
      if (persistedProjectId) {
        await setMemberAccess({
          projectId: persistedProjectId,
          userId: userId as Id<"users">,
          hasAccess: false,
        });
        toast.success("Member access removed");
      } else {
        onSelectedUserIdsChange?.(
          selectedUserIds.filter((selectedUserId) => selectedUserId !== userId)
        );
      }
    } catch (error) {
      notifyError(error, "Failed to update project access");
      console.error("Access update error:", error);
    } finally {
      setUpdatingMemberId(null);
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    props.onOpenChange(nextOpen);
    if (!nextOpen) {
      setSearch("");
      setUpdatingMemberId(null);
    }
  };

  return (
    <Dialog open={props.open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Project Access</DialogTitle>
          <DialogDescription>
            Manage member assignments
            {props.projectName ? (
              <>
                {" "}
                for <span className="font-semibold">{props.projectName}</span>
              </>
            ) : null}
            . Owners and admins always have access.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="project-access-search" className="text-sm font-medium">
              Add company member
            </label>
            <div className="relative">
              <Search className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="project-access-search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search by name or email"
                className="ps-9"
                autoComplete="off"
              />
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="size-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Search Results
                </p>
                <div className="max-h-48 space-y-2 overflow-y-auto pe-1">
                  {addableMembers.length > 0 ? (
                    addableMembers.map((member) => {
                      const displayName = getDisplayName(member);
                      const isUpdating = updatingMemberId === member._id;

                      return (
                        <div
                          key={member._id}
                          className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2"
                        >
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium">{displayName}</p>
                            <p className="truncate text-xs text-muted-foreground">
                              {member.email}
                            </p>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => void handleGrantAccess(member._id)}
                            disabled={isUpdating || updatingMemberId !== null}
                          >
                            {isUpdating ? (
                              <Loader2 className="size-4 animate-spin" />
                            ) : (
                              <Check className="size-4" />
                            )}
                            Add
                          </Button>
                        </div>
                      );
                    })
                  ) : (
                    <div className="rounded-lg border border-dashed px-3 py-6 text-center text-sm text-muted-foreground">
                      {normalizedSearch
                        ? "No matching company members available to add."
                        : "Search or choose from the first available company members."}
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Current Access
                </p>
                <div className="max-h-60 space-y-2 overflow-y-auto pe-1">
                  {currentMembers.length > 0 ? (
                    currentMembers.map((member) => {
                      const displayName = getDisplayName(member);
                      const isOrgManager = !isMemberRole(member);
                      const isLocked = lockedUserIdSet.has(member._id);
                      const isUpdating = updatingMemberId === member._id;

                      return (
                        <div
                          key={member._id}
                          className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2"
                        >
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium">{displayName}</p>
                            <p className="truncate text-xs text-muted-foreground">
                              {member.email}
                            </p>
                            <div className="mt-1 flex flex-wrap items-center gap-2">
                              <RoleBadge role={member.orgRole ?? "member"} />
                              {isOrgManager ? (
                                <span className="text-xs text-muted-foreground">
                                  always allowed
                                </span>
                              ) : null}
                              {isLocked ? (
                                <span className="text-xs text-muted-foreground">
                                  {lockedUserLabel}
                                </span>
                              ) : null}
                            </div>
                          </div>
                          {isMemberRole(member) && !isLocked ? (
                            <IconButton
                              label={`Remove project access for ${displayName}`}
                              icon={isUpdating ? Loader2 : X}
                              iconClassName={isUpdating ? "animate-spin" : undefined}
                              className="text-muted-foreground hover:text-destructive"
                              onClick={() => void handleRevokeAccess(member._id)}
                              disabled={isUpdating || updatingMemberId !== null}
                            />
                          ) : null}
                        </div>
                      );
                    })
                  ) : (
                    <div className="rounded-lg border border-dashed px-3 py-6 text-center text-sm text-muted-foreground">
                      No project members added yet.
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
